import type { MailboxCollectorMappingAddon } from 'owa-analytics-types';
import type MailboxInfo from 'owa-client-types/lib/MailboxInfo';
import type { AccountKey, MailboxType } from 'owa-client-types';
import { getAnalyticsAddon } from 'owa-analytics-addons';
import { getAccountKeyForMailboxInfo } from 'owa-client-types';
import { sendConfigToWorkers } from 'owa-worker-config-sync-send';
import { trace } from 'owa-trace';

let noGlobalAccountIsSynced = true;

export async function syncMailboxCollectorUrlMapping(
    collectorUrl: string | undefined,
    mailboxInfo: MailboxInfo,
    multiAccountType: 'GlobalAccount' | 'SecondaryAccount'
): Promise<void> {
    if (!collectorUrl) {
        trace.warn(
            `[CollectorUrl] No collectorUrl found for mailbox: ${mailboxInfo.mailboxSmtpAddress}`,
            'analytics'
        );
        return;
    }

    const accountKey = getAccountKeyForMailboxInfo(mailboxInfo);
    const mailboxType: MailboxType = mailboxInfo.type;

    await setMailboxCollectorUrlMappingEntry(
        accountKey,
        collectorUrl,
        multiAccountType,
        mailboxType,
        undefined,
        mailboxInfo.mailboxSmtpAddress
    );

    // For the global account, we set a special mapping to make easier to query
    if (noGlobalAccountIsSynced) {
        noGlobalAccountIsSynced = false;
        await setMailboxCollectorUrlMappingEntry(
            'globalAccount',
            collectorUrl,
            'GlobalAccount',
            mailboxType,
            accountKey,
            mailboxInfo.mailboxSmtpAddress
        );
    }
}

async function setMailboxCollectorUrlMappingEntry(
    accountKey: AccountKey | 'globalAccount',
    collectorUrl: string,
    multiAccountType: 'GlobalAccount' | 'SecondaryAccount',
    mailboxType: MailboxType,
    alternateAccountKey?: AccountKey,
    mailboxSmtpAddress?: string
): Promise<void> {
    trace.info(
        `[CollectorUrl] Syncing collectorUrl mapping for accountKey: ${accountKey}, collectorUrl: ${collectorUrl}, mailboxSmtpAddress: ${mailboxSmtpAddress}`,
        'analytics'
    );

    // First, sync it on the main thread
    const addon = await getAnalyticsAddon<MailboxCollectorMappingAddon>(
        'MailboxCollectorMapping'
    )?.executeWhenReadyAsync();
    addon?.setMailboxCollectorUrlMapping(
        accountKey,
        collectorUrl,
        multiAccountType,
        mailboxType,
        alternateAccountKey
    );

    // Then, send it to the workers
    sendConfigToWorkers('all', 'mailboxCollectorMapping', {
        accountKey,
        collectorUrl,
        multiAccountType,
        mailboxType,
        alternateAccountKey,
    });
}
